import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { find, forEach, get, groupBy, map, maxBy, minBy, keys, has } from 'lodash';

import Config from 'config';
import { MODES } from 'constants';

import API from 'lib/tessitura/API';
import features from 'lib/common/features';
import { redirectWithError } from 'lib/common/fallbacks';
import { prepareColorCode } from 'lib/common/colorCode';
import filterPastPerformances from 'lib/common/filterPastPerformances';

import SYOSContainer from 'containers/SYOSContainer';
import LevelSelectorContainer from 'containers/LevelSelectorContainer';
import LoadingSpinner from 'components/LoadingSpinner';

let config;

class AppContainer extends Component {
  constructor(props) {
    super(props);
    config = new Config().getConfig();

    /**
     * If we are provided the MOS and promo/source code
     * ahead of time we can pre-populate loginInfo
     */
    let loginInfo = null;
    if (this.props.initialMos && props.initialPromoCode) {
      loginInfo = {
        MOS: props.initialMos,
        promotion_code: props.initialPromoCode
      };
    }

    this.state = {
      loading: true,
      loginInfo: loginInfo,
      currentFacility: null,
      usesGeneratedSeatmap: true,
      seatData: [],
      screenData: [],
      screenZoneData: null,
      performanceDetails: null,
      packageDetails: null,
      productionDetails: null,
      performanceGroupNo: null,
      performances: [],
      details: null,
      prices: [],
      additionalPrices: [],
      activePromoCode: null,
      selectedLevel: null,
      priceTypes: [],
      additionalPriceTypes: [],
      colours: []
    };

    this.getLoginInfo = this.getLoginInfo.bind(this);
    this.fetchCoreData = this.fetchCoreData.bind(this);
    this.onLevelSelected = this.onLevelSelected.bind(this);
    this.onChangePerformance = this.onChangePerformance.bind(this);
    this.onPromoCodeReady = this.onPromoCodeReady.bind(this);
  }

  componentDidMount() {
    /* global __DEV__ */
    if (__DEV__) {
      features.logEnabledFeatures();
    }

    API.configure(this.props.endpoint, this.props.initialSessionKey);

    API.getSessionKey().then(() => {
      this.getLoginInfo().then(loginInfo => {
        this.setState({
          loginInfo: loginInfo,
        });

        this.fetchCoreData(this.props.initialPerformanceId, loginInfo);
      }, redirectWithError);
    }).catch(redirectWithError);
  }

  getLoginInfo() {
    if (this.state.loginInfo) {
      return Promise.resolve(this.state.loginInfo);
    } else {
      return API.loginInfo();
    }
  }

  fetchCoreData(performanceId, loginInfo) {
    const { mode, packageNo, priceTypeOptions, returnUrl } = this.props;

    // We'll update the entire state in one go to reduce re-renders
    const state = {};

    // We will always need a performance, just grab it first :shrug:
    return API.performanceDetailWithDiscounting(performanceId, packageNo, loginInfo.MOS, loginInfo.promotion_code).then(response => {
      // Check if performance is actually available to purchase
      if (response.Performance.on_sale_ind !== true) {
        // Notify user
        alert('This performance is unavailable. Returning you to the event page.');

        return (window.location.href = returnUrl);
      }

      // Keep track of performance details
      state.performanceDetails = response;

      // Do we have facility config?
      const currentFacility = find(config.facilityMapping, item => {
        return item.facility_no === state.performanceDetails.Performance.facility_no;
      });

      state.currentFacility = currentFacility;
      state.usesGeneratedSeatmap = !currentFacility;

      // The rest can happen at once
      const promises = [];

      // We need package details when in package mode
      if (mode === MODES.FLEX_PACKAGE_PERFORMANCE) {
        const packageDetails = API.getNFSPackageDetail(packageNo, performanceId, loginInfo.MOS, loginInfo.promotion_code).then(response => {
          // Check if package is actually available to purchase
          if (response.Package.on_sale_ind !== true) {
            // Notify user
            alert('This package is unavailable. Returning you to the event page.');

            return (window.location.href = returnUrl);
          }

          // Keep track of details for later
          state.packageDetails = response;

          // We need performances from the same production allowed in
          // the package if performance selector is enabled
          if (features.isEnabled('performanceSelector')) {
            state.performances = state.packageDetails.Performance.filter(performance => {
              return performance.prod_season_no === state.performanceDetails.Performance.prod_season_no;
            });

            state.performances = filterPastPerformances(state.performances);
          }

          // We'll need performance group to add to cart, extract it here
          state.performanceGroupNo = state.packageDetails.Performance.find(perf => {
            return perf.perf_no === performanceId;
          }).perf_group_no;
        });

        promises.push(packageDetails);
      } else if (mode === MODES.PERFORMANCE) {
        // We'll only need performances if performance selector is enabled
        // therefore optionally need to make the production call
        if (features.isEnabled('performanceSelector')) {
          const productionDetails = API.getProductionDetail(state.performanceDetails.Performance.prod_season_no).then(response => {
            state.productionDetails = response;
            state.performances = filterPastPerformances(response.Performance);
          });

          promises.push(productionDetails);
        }
      }

      const seatingDetails = API.getSeatmapData(
        performanceId,
        undefined,
        state.performanceDetails.Performance.facility_no,
        loginInfo.MOS,
        loginInfo.promotion_code,
        this.state.usesGeneratedSeatmap,
      ).then(response => {
        state.seatData = get(response, 'seatsResults');
        state.screenData = get(response, 'screensResults.Screens');
        state.screenZoneData = get(response, 'screenZoneData');
      });

      promises.push(seatingDetails);

      return Promise.all(promises).then(() => {
        const { packageDetails, performanceDetails } = state;

        if (mode === MODES.FLEX_PACKAGE_PERFORMANCE) {
          // Grab package price types
          state.priceTypes = packageDetails.PriceType;

          // Further filtering package specific price types?
          const allowedPriceTypes = get(priceTypeOptions, 'primaryPriceTypes', null);

          if (allowedPriceTypes) {
            state.priceTypes = state.priceTypes.filter(type => {
              return keys(allowedPriceTypes).indexOf(String(type.price_type)) !== -1;
            });
          }

          // We need IDs to filter on
          const priceTypeIds = state.priceTypes.map(type => {
            return type.price_type;
          });

          state.prices = performanceDetails.AllPrice.filter(price => {
            return priceTypeIds.indexOf(price.price_type) !== -1;
          }).sort((a, b) => {
            return priceTypeIds.indexOf(a.price_type) - priceTypeIds.indexOf(b.price_type);
          });

          // Grab performance price types
          state.additionalPriceTypes = performanceDetails.PriceType;

          // Further filtering performance specific price types?
          const allowedAdditionalPriceTypes = get(priceTypeOptions, 'addOnPriceTypes', null);

          if (allowedAdditionalPriceTypes) {
            state.additionalPriceTypes = state.additionalPriceTypes.filter(type => {
              return keys(allowedAdditionalPriceTypes).indexOf(String(type.price_type)) !== -1;
            });
          }

          // We need IDs to filter on
          const additionalPriceTypeIds = state.additionalPriceTypes.map(type => {
            return type.price_type;
          });

          state.additionalPrices = performanceDetails.AllPrice.filter(price => {
            return additionalPriceTypeIds.indexOf(price.price_type) !== -1;
          }).sort((a, b) => {
            return additionalPriceTypeIds.indexOf(a.price_type) - additionalPriceTypeIds.indexOf(b.price_type);
          });
        } else {
          state.priceTypes = performanceDetails.PriceType;

          // Further filtering package specific price types?
          const allowedPriceTypes = get(priceTypeOptions, 'primaryPriceTypes', null);

          if (allowedPriceTypes) {
            state.priceTypes = state.priceTypes.filter(type => {
              return keys(allowedPriceTypes).indexOf(String(type.price_type)) !== -1;
            });
          }

          // We need IDs to filter on
          const priceTypeIds = state.priceTypes.map(type => {
            return type.price_type;
          });

          state.prices = performanceDetails.AllPrice.filter(price => {
            return priceTypeIds.indexOf(price.price_type) !== -1;
          }).sort((a, b) => {
            return priceTypeIds.indexOf(a.price_type) - priceTypeIds.indexOf(b.price_type);
          });

          // Grab performance price types
          state.additionalPriceTypes = performanceDetails.PriceType;

          // Further filtering performance specific price types?
          const allowedAdditionalPriceTypes = get(priceTypeOptions, 'addOnPriceTypes', null);

          if (allowedAdditionalPriceTypes) {
            state.additionalPriceTypes = state.additionalPriceTypes.filter(type => {
              return keys(allowedAdditionalPriceTypes).indexOf(String(type.price_type)) !== -1;
            });
          }

          // We need IDs to filter on
          const additionalPriceTypeIds = state.additionalPriceTypes.map(type => {
            return type.price_type;
          });

          state.additionalPrices = performanceDetails.AllPrice.filter(price => {
            return additionalPriceTypeIds.indexOf(price.price_type) !== -1;
          }).sort((a, b) => {
            return additionalPriceTypeIds.indexOf(a.price_type) - additionalPriceTypeIds.indexOf(b.price_type);
          });
        }

        // Work out availability based on zones
        forEach(state.screenZoneData, (item, screen) => {
          let zonePriceTypes = state.prices.filter(price => {
            return item.zones.indexOf(price.zone_no) !== -1;
          });

          zonePriceTypes = groupBy(zonePriceTypes, 'price_type');

          zonePriceTypes = map(zonePriceTypes, priceTypes => {
            const minPrice = minBy(priceTypes, 'price');
            const maxPrice = maxBy(priceTypes, 'price');

            let availability = [];

            priceTypes.forEach(priceType => {
              availability.push(priceType.avail_count);
            });

            return {
              price_type: priceTypes[0].price_type,
              description: priceTypes[0].description,
              minPrice: parseFloat(minPrice.price) ,
              maxPrice: parseFloat(maxPrice.price) ,
              availability: availability ,
            };
          });

          item.screen = parseInt(screen, 10);
          item.avail = zonePriceTypes.length ? zonePriceTypes[0].availability.reduce((a, b) => a + b, 0) : 0;
          item.priceTypes = zonePriceTypes;
        });

        const customColors = state.currentFacility && has(state.currentFacility, 'customColorCodes') ? state.currentFacility.customColorCodes : null;

        this.setState({
          ...state,
          colours: prepareColorCode(state.prices, state.additionalPrices, customColors),
          loading: false,
        })
      });
    });
  }

  onLevelSelected(selector, isGa, newZoom, extraParams, isEmbed) {
    const { initialPerformanceId, initialSessionKey, initialPromoCode } = this.props;

    const bestAvailableUrl = features.getOption('levelSelector', 'bestAvailableUrl');

    const fullScreenUrl = features.getOption('levelSelector', 'fullScreenURL');

    if (isGa && bestAvailableUrl && initialPerformanceId) {
      if (extraParams) {
        window.location = `${bestAvailableUrl}${initialPerformanceId}?${extraParams}`;
      } else {
        window.location = `${bestAvailableUrl}${initialPerformanceId}`;
      }
    } else if (isEmbed && fullScreenUrl && initialPerformanceId) {
      let locationParts = [
        `layer=${selector}`, `perf_no=${initialPerformanceId}`,
      ];

      if (initialSessionKey) {
        locationParts.push(`session_key=${initialSessionKey}`);
      }

      if (initialPromoCode) {
        locationParts.push(`source_code=${initialPromoCode}`);
      }

      locationParts = locationParts.join('&');

      window.location = `${fullScreenUrl}&${locationParts}`;
    } else {
      this.setState({
        selectedLevel: {
          selector: selector,
          newZoom: newZoom
        }
      });
    }
  }

  onChangePerformance(performanceId) {
    this.setState({
      details: null,
      prices: [],
      seatData: [],
      screenData: [],
      loading: true
    });

    this.fetchCoreData(performanceId, this.state.loginInfo);
  }

  onPromoCodeReady(activePromoCode) {
    if (activePromoCode) {
      this.setState({
        activePromoCode: activePromoCode
      });
    }
  }

  render() {
    if (this.state.loading) {
      return (
        <LoadingSpinner />
      );
    }

    const levelSelectorConfig = get(this.state.currentFacility, 'levelSelector');

    const shouldRenderLevelSelector =
      features.isEnabled('levelSelector') &&
      levelSelectorConfig &&
      !this.state.selectedLevel &&
      !this.props.queryParams.skip_level_selector;

    if (shouldRenderLevelSelector) {
      return (
        <LevelSelectorContainer
          performanceId={this.props.initialPerformanceId}
          levelSelectorConfig={levelSelectorConfig}
          screenZoneData={this.state.screenZoneData}
          onPromoCodeReady={this.onPromoCodeReady}
          onLevelSelected={this.onLevelSelected}
        />
      );
    }

    return this.state.loginInfo ? (
      <SYOSContainer
        initialPerformanceId={this.props.initialPerformanceId}
        packageNo={this.props.packageNo}
        performanceGroupNo={this.state.performanceGroupNo}
        loginInfo={this.state.loginInfo}
        performanceDetails={this.state.performanceDetails}
        packageDetails={this.state.packageDetails}
        prices={this.state.prices}
        additionalPrices={this.state.additionalPrices}
        priceTypes={this.state.priceTypes}
        additionalPriceTypes={this.state.additionalPriceTypes}
        seatData={this.state.seatData}
        screenData={this.state.screenData}
        screenZoneData={this.state.screenZoneData}
        selectedLevel={this.state.selectedLevel}
        currentFacility={this.state.currentFacility}
        usesGeneratedSeatmap={this.state.usesGeneratedSeatmap}
        activePromoCode={this.state.activePromoCode}
        onChangePerformance={this.onChangePerformance}
        onPromoCodeReady={this.onPromoCodeReady}
        onComplete={this.props.onComplete}
        colours={this.state.colours}
        performances={this.state.performances}
        mode={this.props.mode}
        priceTypeOptions={this.props.priceTypeOptions}
        returnUrl={this.props.returnUrl}
      />
    ) : null;
  }
}

AppContainer.propTypes = {
  endpoint: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
  initialSessionKey: PropTypes.string,
  initialPerformanceId: PropTypes.number.isRequired,
  initialMos: PropTypes.number,
  initialPromoCode: PropTypes.number,
  packageNo: PropTypes.number,
  queryParams: PropTypes.object,
  mode: PropTypes.string,
  priceTypeOptions: PropTypes.object,
  returnUrl: PropTypes.string,
};

AppContainer.defaultProps = {
  mode: MODES.PERFORMANCE,
  returnUrl: '/',
};

export default AppContainer;



// WEBPACK FOOTER //
// ./src/javascripts/containers/AppContainer.js